import {
  Button,
  Flex,
  Grid,
  Heading,
  IconButton,
} from "@sidelinesports/fan-design-system";
import { CancelIcon, Die3Icon } from "@sidelinesports/icons";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { Page } from "../../analytics/Page.tsx";
import type {
  ApiGameDetails,
  ApiPick,
  ApiPlayerDetails,
} from "../../api/index.ts";
import { Dialog, FullscreenFixedLayout } from "../../components/index.tsx";
import { ErrorScreen, LoadingScreen } from "../../screens/index.ts";
import { GameWelcomeMessageDialog } from "../GameWelcomeDialog/GameWelcomeMessageDialog.tsx";
import { useMessageViewTracking } from "../GameWelcomeDialog/hooks/use-message-view-tracking.ts";
import { PlayerCard, ScoreLegend } from "./components/index.tsx";
import {
  useGameDetailsQuery,
  usePicksQuery,
  usePlayersQuery,
} from "./hooks/data.ts";
import { usePlayerSelectionForm } from "./hooks/form.ts";
import { useRedirectIfGameFinished } from "./hooks/use-redirect-if-game-finished.ts";

export const PickYourPlayersPage = () => {
  const { gameId = "" } = useParams<{ gameId: string }>();

  const gameDetailsQuery = useGameDetailsQuery(gameId);
  const picksQuery = usePicksQuery(gameId);
  const playersQuery = usePlayersQuery();

  const { hasAlreadyViewed: hasAlreadyViewedWelcomeMessage } =
    useMessageViewTracking(gameId, { recordView: false });

  const gamePlayers = useMemo(
    () =>
      playersQuery.data?.filter((player) =>
        gameDetailsQuery.data?.gamePlayers.some(
          (gamePlayer) => gamePlayer.playerId === player.id,
        ),
      ) || [],
    [gameDetailsQuery.data, playersQuery.data],
  );

  if (
    gameDetailsQuery.isPending ||
    picksQuery.isPending ||
    playersQuery.isPending
  ) {
    return <LoadingScreen />;
  }

  if (gameDetailsQuery.isError || picksQuery.isError || playersQuery.isError) {
    return (
      <ErrorScreen
        error={gameDetailsQuery.error || picksQuery.error || playersQuery.error}
      />
    );
  }

  return (
    <Page name="PickYourPlayers">
      {gameDetailsQuery.data.prizes.length > 0 &&
        !hasAlreadyViewedWelcomeMessage && (
          <Dialog.Root defaultOpen={true}>
            <GameWelcomeMessageDialog name="GameWelcomeMessageDialog" />
          </Dialog.Root>
        )}
      <PickYourPlayersForm
        game={gameDetailsQuery.data}
        gamePlayers={gamePlayers}
        picks={picksQuery.data}
      />
    </Page>
  );
};

const PickYourPlayersForm = ({
  game,
  gamePlayers,
  picks,
}: {
  game: ApiGameDetails;
  gamePlayers: ApiPlayerDetails[];
  picks: ApiPick[];
}) => {
  const navigate = useNavigate();

  useRedirectIfGameFinished();

  const {
    selectedPlayers,
    addPlayer,
    randomizeSelections,
    removePlayer,
    isPlayerSelected,
  } = usePlayerSelectionForm(picks, gamePlayers);

  return (
    <FullscreenFixedLayout
      header={<PickYourPlayersHeader />}
      footer={
        <PickYourPlayersFooter
          onRandomizePlayers={() => {
            randomizeSelections(gamePlayers);
          }}
          onContinue={
            selectedPlayers.length === 3
              ? () => {
                  const selectedPlayerIds = selectedPlayers.map(
                    (player) => player.id,
                  );

                  const params = new URLSearchParams();
                  params.set("playerIds", selectedPlayerIds.join(","));

                  navigate(`../powerup-your-players?${params.toString()}`);
                }
              : undefined
          }
        />
      }
    >
      <Grid columns="repeat(auto-fill, minmax(9rem, 1fr))" gap={16} p={16}>
        <ScoreLegend trigger={<ScoreLegendTriggerButton />} game={game} />
        {gamePlayers.map((player) => (
          <PlayerCard
            key={player.id}
            player={player}
            disabled={
              selectedPlayers.length >= 3 && !isPlayerSelected(player.id)
            }
            selected={isPlayerSelected(player.id)}
            onClick={() => {
              if (isPlayerSelected(player.id)) {
                removePlayer(player.id);
              } else {
                addPlayer(player);
              }
            }}
          />
        ))}
      </Grid>
    </FullscreenFixedLayout>
  );
};

const ScoreLegendTriggerButton = () => (
  <Dialog.Trigger
    as={Button}
    variant="outline"
    size="sm"
    style={{ gridColumn: "1 / -1" }}
  >
    Score Legend
  </Dialog.Trigger>
);

const PickYourPlayersHeader = () => {
  const navigate = useNavigate();

  return (
    <Flex align="center" gap={8} p={16}>
      <Heading level={1} style={{ flexGrow: 1, textTransform: "uppercase" }}>
        Pick Your Players
      </Heading>
      <IconButton
        variant="outline-light"
        size="lg"
        icon={<CancelIcon />}
        aria-label="Close"
        onClick={() => navigate(-1)}
      />
    </Flex>
  );
};

const PickYourPlayersFooter = ({
  onRandomizePlayers,
  onContinue,
}: {
  onRandomizePlayers?: () => void;
  onContinue?: () => void;
}) => (
  <Flex
    justify="center"
    p={16}
    gap={16}
    style={{ boxShadow: "0px -4px 16px 0px rgba(0, 0, 0, 0.40)" }}
  >
    <IconButton
      variant="outline-light"
      size="lg"
      icon={<Die3Icon />}
      aria-label="Randomize Players"
      style={{ stroke: "currentColor", flexShrink: 0, flexGrow: 0 }}
      onClick={onRandomizePlayers}
    />
    <Button
      variant="action"
      size="sm"
      style={{ flexGrow: 1 }}
      onClick={onContinue}
      disabled={!onContinue}
    >
      Continue
    </Button>
  </Flex>
);
